import { MessageSquareIcon, StickyNoteIcon } from 'lucide-react'
import { type FC, memo, type ReactNode, useCallback } from 'react'

import { cn } from '@/lib/utils'
import type { ChatInputMode } from './chat-input-mode.type'
import { ChatInputModeButton } from './chat-input-mode-button.atom'
import { MESSAGE_SELECTED_CLASS, NOTE_SELECTED_CLASS } from './chat-input-mode-switch.config'

export interface ChatInputModeSwitchProps {
  mode: ChatInputMode
  onModeChange: (mode: ChatInputMode) => void
  isDisabled?: boolean
  className?: string
}

const ChatInputModeSwitchBase: FC<ChatInputModeSwitchProps> = ({
  mode,
  onModeChange,
  isDisabled = false,
  className,
}): ReactNode => {
  const createModeHandler = useCallback(
    (newMode: ChatInputMode) => () => {
      if (!isDisabled) onModeChange(newMode)
    },
    [isDisabled, onModeChange]
  )

  return (
    <div
      className={cn(
        'chat-input-mode-switch inline-flex items-center rounded-lg border bg-muted/50 p-0.5',
        isDisabled && 'pointer-events-none opacity-50',
        className
      )}
      role="tablist"
      aria-label="Message type"
    >
      <ChatInputModeButton
        icon={MessageSquareIcon}
        label="Message"
        tooltipText="Send message to agent"
        isSelected={mode === 'message'}
        isDisabled={isDisabled}
        onClick={createModeHandler('message')}
        className="chat-input-mode-message"
        selectedClassName={MESSAGE_SELECTED_CLASS}
      />
      <ChatInputModeButton
        icon={StickyNoteIcon}
        label="Note"
        tooltipText="Add note for team only"
        isSelected={mode === 'note'}
        isDisabled={isDisabled}
        onClick={createModeHandler('note')}
        className="chat-input-mode-note"
        selectedClassName={NOTE_SELECTED_CLASS}
      />
    </div>
  )
}

export const ChatInputModeSwitch = memo(ChatInputModeSwitchBase)

ChatInputModeSwitch.displayName = 'ChatInputModeSwitch'
